import { ReactNode } from 'react'
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react'
import { Button } from './Button'

interface ErrorStateProps {
  title?: ReactNode
  text?: ReactNode
  /** Loads again. Without it there is no Retry button. */
  onRetry?: () => void
  /** True while the retry runs: the button says so and ignores more clicks. */
  retrying?: boolean
  retryLabel?: string
  icon?: ReactNode
  /** Another way out, shown next to Retry. */
  action?: ReactNode
  className?: string
}

/**
 * What a list or panel shows when it couldn't load, with a Retry. Use it
 * instead of the empty state, so a failed load never reads as "nothing to do".
 */
export function ErrorState({
  title = "Couldn't load this",
  text,
  onRetry,
  retrying = false,
  retryLabel = 'Retry',
  icon,
  action,
  className = '',
}: ErrorStateProps) {
  return (
    <div role="alert" className={`flex flex-col items-center justify-center px-6 py-10 text-center ${className}`}>
      <div
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-danger/10 text-danger"
        aria-hidden="true"
      >
        {icon ?? <AlertTriangle size={18} />}
      </div>
      <p className="font-display text-base font-semibold text-foreground">{title}</p>
      {text ? <div className="mt-1 max-w-md text-sm text-foreground-muted">{text}</div> : null}
      {onRetry || action ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {onRetry ? (
            <Button
              type="button"
              variant="secondary"
              icon={retrying ? <Loader2 size={13} className="animate-spin" /> : <RotateCw size={13} />}
              aria-disabled={retrying || undefined}
              onClick={() => {
                // aria-disabled rather than disabled: a disabled button would drop
                // keyboard focus while the retry runs.
                if (!retrying) onRetry()
              }}
            >
              {retrying ? 'Retrying…' : retryLabel}
            </Button>
          ) : null}
          {action}
        </div>
      ) : null}
    </div>
  )
}
